import { useListaDeGmuds } from '../../api/queries/gmuds'
import {
  AGUARDANDO_AJUSTES,
  AGUARDANDO_COMITE,
  APROVADAS_NAO_EXECUTADAS,
  EM_CURSO,
  filtrosDoRecorte,
  HISTORICO_IMPORTADO,
  TODO_O_PROCESSO,
  type RecorteDoPainel,
} from './recortes'

/**
 * As contagens do painel, todas pela mesma consulta da listagem.
 *
 * Uma página de tamanho 1 por recorte: o que se lê é o `totalElementos` do envelope, e o conteúdo
 * vem junto só porque a API não tem um endpoint que devolva contagem sozinha. São seis consultas
 * no carregamento da tela — o mesmo `count(*)` que o rodapé da lista já faz, seis vezes. Sai mais
 * barato do que uma segunda montagem de filtro no servidor, que é onde os números começam a
 * divergir de tela para tela.
 */
const SO_O_TOTAL = 1

export interface Contagem {
  /**
   * Nulo enquanto não há resposta, e não zero.
   *
   * Zero é uma afirmação — "não existe nenhuma" —, e enquanto a consulta não voltou o painel não
   * sabe disso. Mostrar zero durante o carregamento faria a tela dizer que não há nada esperando
   * o comitê justamente para quem abriu o sistema com pressa.
   */
  quantidade: number | null
  carregando: boolean
  erro: unknown
}

export interface CartaoDoPainel {
  recorte: RecorteDoPainel
  contagem: Contagem
}

/**
 * Quantas solicitações atendem a um recorte.
 *
 * @param recorte o recorte do painel
 * @returns a contagem, o estado de carregamento e o erro do servidor
 */
export function useContagem(recorte: RecorteDoPainel): Contagem {
  const { data, isPending, error } = useListaDeGmuds(filtrosDoRecorte(recorte, SO_O_TOTAL))
  return { quantidade: data?.totalElementos ?? null, carregando: isPending, erro: error }
}

export interface ContagensDoPainel {
  /** Os cartões da grade, na ordem em que aparecem. */
  cartoes: CartaoDoPainel[]
  /** Tudo o que nasceu neste sistema. É o denominador, e o que decide se a tela está vazia. */
  processo: Contagem
  /** O histórico importado, contado à parte e nunca somado aos cartões. */
  historico: Contagem
  /** O primeiro erro que apareceu, para a página explicar uma vez o que o servidor respondeu. */
  erro: unknown
}

/**
 * Todas as contagens da tela.
 *
 * As chamadas estão escritas uma a uma, e não num laço sobre uma lista de recortes: hook dentro de
 * laço quebra a regra dos hooks do React no dia em que a lista mudar de tamanho — e a ordem dos
 * cartões é decisão de conteúdo, que fica aqui à vista em vez de espalhada.
 *
 * @returns as contagens dos cartões, do processo e do histórico
 */
export function useContagensDoPainel(): ContagensDoPainel {
  const aguardandoComite = useContagem(AGUARDANDO_COMITE)
  const aguardandoAjustes = useContagem(AGUARDANDO_AJUSTES)
  const aprovadas = useContagem(APROVADAS_NAO_EXECUTADAS)
  const emCurso = useContagem(EM_CURSO)
  const processo = useContagem(TODO_O_PROCESSO)
  const historico = useContagem(HISTORICO_IMPORTADO)

  const cartoes: CartaoDoPainel[] = [
    { recorte: AGUARDANDO_COMITE, contagem: aguardandoComite },
    { recorte: AGUARDANDO_AJUSTES, contagem: aguardandoAjustes },
    { recorte: APROVADAS_NAO_EXECUTADAS, contagem: aprovadas },
    { recorte: EM_CURSO, contagem: emCurso },
  ]

  const erro = [...cartoes.map(({ contagem }) => contagem.erro), processo.erro, historico.erro].find(Boolean)

  return { cartoes, processo, historico, erro: erro ?? null }
}

/**
 * O número como se escreve em português.
 *
 * Com 1.027 registros importados o separador de milhar não é enfeite: "1027" na cara de um fiscal
 * de contrato se lê duas vezes antes de virar mil e vinte e sete.
 *
 * @param quantidade o total contado
 * @returns o número com separador de milhar
 */
export function contagemPorExtenso(quantidade: number): string {
  return quantidade.toLocaleString('pt-BR')
}
